import React, { useEffect } from 'react';
import Routes from 'routes/Routes';
import AuthProvider from 'context/AuthContext';
import QueueProvider from 'context/QueueContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'react-loading-skeleton/dist/skeleton.css';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, useLocation } from 'react-router-dom';
import PageLoaderProvider from 'context/PageLoader';
import JobDetailSidebarProvider from 'context/JobDetailSidebarContext';
import ReactGA from 'react-ga4';
import { PostHogProvider } from 'posthog-js/react';
import ErrorBoundary from 'components/ErrorBoundary';
import PostHogUserTracker from 'components/PostHogUserTracker';
import ZendeskRouteVisibility from 'components/ZendeskRouteVisibility';
import { appConfig } from 'appConfig';
import { sendPageView } from 'utils/segment';

const isDevelopment = process.env.NODE_ENV === 'development';

const ReactQueryDevtools = isDevelopment
  ? React.lazy(async () => {
      const mod = await import('@tanstack/react-query-devtools');
      return { default: mod.ReactQueryDevtools };
    })
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Don't retry on client errors (4xx)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 2 times for network errors and server errors
        return failureCount < 2;
      },
      staleTime: 1 * 60 * 1000,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry once for network/server errors
        return failureCount < 1;
      },
    },
  },
});

if (process.env.NODE_ENV === 'production') {
  ReactGA.initialize('G-SRF0BGG0CQ');
}

// Component to track route changes for Segment
const SegmentPageTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const enableSegment = appConfig.enableSegment;
    if (enableSegment) {
      sendPageView();
    }
  }, [location.pathname, location.search]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname + window.location.search,
      });
    }
  }, []);

  return (
    <PostHogProvider
      apiKey={appConfig.posthogApiKey}
      options={{
        api_host: 'https://us.i.posthog.com',
        defaults: '2025-05-24',
        person_profiles: 'identified_only',
        segment: (window as any).analytics,
        capture_pageview: false,
      }}
    >
      <BrowserRouter>
        <SegmentPageTracker />
        <ZendeskRouteVisibility />
        <PageLoaderProvider>
          <AuthProvider>
            <PostHogUserTracker />
            <QueueProvider>
              <QueryClientProvider client={queryClient} contextSharing>
                <JobDetailSidebarProvider>
                  <ErrorBoundary>
                    <Routes />
                  </ErrorBoundary>
                </JobDetailSidebarProvider>
                {isDevelopment && ReactQueryDevtools ? (
                  <React.Suspense fallback={null}>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </React.Suspense>
                ) : null}
              </QueryClientProvider>
            </QueueProvider>
          </AuthProvider>
        </PageLoaderProvider>

        <Toaster
          position="top-center"
          containerStyle={{
            zIndex: 999999,
            color: 'black',
          }}
          toastOptions={{
            duration: 5000,
            error: {
              style: {
                color: '#b91c1c',
                border: '2px solid #b91c1c',
                background: '#fef2f2',
                fontWeight: 'bold',
                padding: '16px 20px',
                boxShadow: '0 2px 8px rgba(185,28,28,0.08)',
                fontSize: '1rem',
                width: 'auto',
                maxWidth: 'none',
                minWidth: 0,
                whiteSpace: 'pre-line',
                display: 'inline-flex',
              },
              iconTheme: {
                primary: '#b91c1c',
                secondary: 'white',
              },
            },
            success: {
              style: {
                color: '#000000',
                border: '1px solid #1B57CA',
                background: '#ffffff',
                fontWeight: 'bold',
                padding: '16px 20px',
                boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
                fontSize: '16px',
                width: 'auto',
                maxWidth: 'none',
                minWidth: 0,
                whiteSpace: 'pre-line',
                display: 'inline-flex',
              },
              iconTheme: {
                primary: 'green',
                secondary: 'white',
              },
            },
          }}
        />
      </BrowserRouter>
    </PostHogProvider>
  );
};

export default App;
